import { useId } from "react";

import ReactDatePicker from "react-datepicker";
import { Controller, useFormContext } from "react-hook-form";
import "react-datepicker/dist/react-datepicker.css";

import Input from "./Input";
import calendarBlankIcon from "../icon/CalendarBlank.svg";

import "../style/datepicker.scss";
import "../style/inputs.scss";

/**
 * Date picker that uses react-datepicker library and supports integration with react-hook-form.
 *
 * react-datepicker examples: https://reactdatepicker.com/
 *
 * NOTE: react-datepicker supports time, but this component hasn't implemented that yet.
 *
 * @param {object} props
 * @param {string} props.label The label text for the input.
 * @param {string} props.name The name of the input for form-related purposes.
 * @param {boolean} props.noShrink If true, component will always leave space for the error message, even if there's no error.
 * @param {Date} props.value The controlled value of the date picker.
 * @param {import("react-hook-form").RegisterOptions} props.formOptions The form options object.
 * @param {Function} props.onChange Callback function when date value changes.
 * @param {boolean} props.disabled If true, the datepicker will be disabled.
 * @param {string} props.dateFormat Format for dates. Documentation for the format string is lacking but may improve in the future: https://github.com/Hacker0x01/react-datepicker/blob/main/docs/datepicker.md
 */
function DatePicker({
    label,
    name,
    noShrink,
    value: controlledValue,
    formOptions,
    onChange,
    disabled,
    dateFormat = "yyyy-MM-dd",
    ...restProps
}) {
    const portalId = useId();

    const formContext = useFormContext();

    /** @type {import("react-datepicker").DatePickerProps} */
    const sharedProps = {
        // The portalId prop renders the datepicker in a portal attached to the document body to prevent layout issues
        // caused by tab loop. Without this, the tab loop adds a hidden DOM element after the input when the popover is
        // open, which causes unwanted spacing in flexbox containers by increasing the gap between items. Using a portal
        // to render the popover prevents this issue because the popover is no longer rendered inside the flexbox. Tab
        // loop is a component used internally by react-datepicker, their documentation for it is here:
        // https://github.com/Hacker0x01/react-datepicker/blob/main/docs/tab_loop.md
        portalId: `${portalId}react-datepicker-portal`,
        customInput: (
            <Input
                reactDatepickerName={name} // Hacky way to prevent react-datepicker from setting name to undefined (setting to undefined causes issues getting errors when using react-hook-form)
                isDatePicker={true}
                label={label}
                svg={calendarBlankIcon}
                noShrink={noShrink}
            />
        ),
        showPopperArrow: false,
        popperPlacement: "bottom",
        dateFormat,
        ...restProps,
    };

    return formContext ? (
        <Controller // react-datepicker is a controlled component, so this is required to use it with react-hook-form https://react-hook-form.com/get-started#IntegratingControlledInputs
            control={formContext?.control}
            rules={formOptions}
            name={name}
            render={({ field }) => (
                <ReactDatePicker {...sharedProps} onChange={field.onChange} selected={field.value} />
            )}
        />
    ) : (
        <ReactDatePicker {...sharedProps} selected={controlledValue} onChange={onChange} />
    );
}

export default DatePicker;
